/**
 * Discord sign-in (Discord build, SY-01.2): OAuth2 authorization code + PKCE with the
 * `openid` scope, exchanged at Discord's token endpoint as a public client (code_verifier,
 * no secret), which returns an id token for the API to verify (POST /v1/auth/link/discord).
 *
 * Two ways to get the code:
 *  - a popup on the web: the redirect page calls completeOAuthPopup();
 *  - inside a Discord Activity (`activity`): the Embedded App SDK's authorize() command,
 *    which shows Discord's own consent modal. There is no redirect page, and the Activity's
 *    CSP only lets requests out through its URL mappings, so `tokenUrl` is the mapped path of
 *    Discord's token endpoint (docs/discord.md), e.g. `/.proxy/discord/api/oauth2/token`.
 */
import { pkceChallenge, randomState, randomVerifier, type OAuthMessage } from './pkce.js';
import { SignInCancelled, type IdentityProvider, type IdentityToken } from './types.js';

/** The Embedded App SDK's `commands.authorize` (DiscordSDK), inside a Discord Activity. */
export type DiscordAuthorize = (args: {
  client_id: string;
  scope: string[];
  response_type: 'code';
  code_challenge: string;
  code_challenge_method: 'S256';
  state: string;
  prompt?: 'none';
}) => Promise<{ code: string }>;

export interface DiscordSignInConfig {
  clientId: string;
  /** the popup flow: a page on this origin that calls completeOAuthPopup() (required there) */
  redirectUri?: string;
  /** space-separated; default `openid identify` (openid is what makes Discord return an id token) */
  scope?: string;
  authorizeUrl?: string;
  /** Discord's token endpoint; inside an Activity its URL-mapped path */
  tokenUrl?: string;
  /** inside a Discord Activity: the SDK's authorize() replaces the popup */
  activity?: { authorize: DiscordAuthorize };
  window?: Window;
  fetch?: typeof fetch;
}

/** RPC error codes and messages of an authorize() the player closed. */
const isCancel = (e: unknown): boolean => {
  const { code, message } = (e ?? {}) as { code?: unknown; message?: unknown };
  return code === 4001 || /cancel|closed|denied/i.test(String(message ?? ''));
};

export class DiscordIdentityProvider implements IdentityProvider {
  readonly provider = 'discord';

  constructor(private readonly c: DiscordSignInConfig) {}

  async signIn(): Promise<IdentityToken> {
    const verifier = randomVerifier();
    const state = randomState();
    const challenge = await pkceChallenge(verifier);
    const scope = this.c.scope ?? 'openid identify';
    let code: string;
    let redirectUri: string | null = null;
    if (this.c.activity) {
      const r = await this.c.activity
        .authorize({ client_id: this.c.clientId, scope: scope.split(' ').filter(Boolean), response_type: 'code', code_challenge: challenge, code_challenge_method: 'S256', state, prompt: 'none' })
        .catch((e: unknown) => {
          throw isCancel(e) ? new SignInCancelled('discord') : e;
        });
      code = r.code;
    } else {
      if (!this.c.redirectUri) throw new Error('discord sign-in: redirectUri is required outside a Discord Activity');
      redirectUri = this.c.redirectUri;
      code = await this.popup(scope, state, challenge, redirectUri);
    }
    const res = await (this.c.fetch ?? fetch)(this.c.tokenUrl ?? 'https://discord.com/api/oauth2/token', {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ grant_type: 'authorization_code', code, client_id: this.c.clientId, code_verifier: verifier, ...(redirectUri ? { redirect_uri: redirectUri } : {}) }),
    });
    if (!res.ok) throw new Error(`discord token exchange failed: HTTP ${res.status}`);
    const tok = (await res.json()) as { id_token?: string };
    if (!tok.id_token) throw new Error('discord returned no id_token (is the openid scope enabled?)');
    return { provider: 'discord', idToken: tok.id_token };
  }

  private async popup(scope: string, state: string, challenge: string, redirectUri: string): Promise<string> {
    const win = this.c.window ?? window;
    const url = new URL(this.c.authorizeUrl ?? 'https://discord.com/oauth2/authorize');
    url.search = new URLSearchParams({
      response_type: 'code',
      client_id: this.c.clientId,
      scope,
      redirect_uri: redirectUri,
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    }).toString();
    const popup = win.open(url.toString(), 'mp-discord-auth', 'popup,width=480,height=720');
    if (!popup) throw new Error('discord sign-in popup was blocked');
    return waitForCode(win, popup, state);
  }
}

function waitForCode(win: Window, popup: Window, state: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const onMessage = (e: MessageEvent<OAuthMessage>) => {
      if (e.origin !== win.location.origin || e.data?.type !== 'mp-oauth' || e.data.state !== state) return;
      done();
      if (e.data.code) resolve(e.data.code);
      else reject(e.data.error === 'access_denied' ? new SignInCancelled('discord') : new Error(`discord oauth: ${e.data.error ?? 'no code'}`));
    };
    const poll = setInterval(() => {
      if (!popup.closed) return;
      done();
      reject(new SignInCancelled('discord'));
    }, 500);
    const done = () => {
      clearInterval(poll);
      win.removeEventListener('message', onMessage);
    };
    win.addEventListener('message', onMessage);
  });
}
